import type { FilterChip, Lookups } from "../../types";
import { renderFilterDetailList } from "../../components/FilterDetailList";
import { mapIdsToNames } from "../../utils/mapIdsToNames";
import { listNames } from "../../utils/listNames";
import type { VendorsSlice } from "./slice";

export function getChips(
  slice: VendorsSlice,
  setSlice: (slice: VendorsSlice) => void,
  lookups?: Lookups
): FilterChip[] {
  const chips: FilterChip[] = [];

  const inclusionCount =
    slice.inclusionVendorIds.length +
    slice.inclusionDepartmentIds.length +
    slice.inclusionRoleIds.length +
    (slice.inclusionInstallAgeMonths ? 1 : 0) +
    (slice.inclusionProductCount ? 1 : 0);

  // Chip for included vendors
  if (inclusionCount > 0) {
    const includedNames = lookups?.vendors
      ? mapIdsToNames(
          slice.inclusionVendorIds,
          lookups.vendors,
          (id) => `Vendor ${id}`
        )
      : [];

    const label =
      slice.inclusionVendorIds.length === 1 && includedNames[0]
        ? includedNames[0]
        : `${inclusionCount} selected`;

    const tooltipContent =
      lookups?.vendors && slice.inclusionVendorIds.length > 0
        ? renderFilterDetailList("Vendors", includedNames)
        : undefined;

    // Until the vendor list loads, the names are "Vendor <id>" placeholders.
    const allNames = lookups?.vendors?.length
      ? listNames(
          includedNames,
          inclusionCount - slice.inclusionVendorIds.length
        )
      : undefined;

    chips.push({
      id: "vendors",
      label: `Vendors: ${label}`,
      summaryLabel: allNames && `Vendors: ${allNames}`,
      filterKey: "vendors" as const,
      onRemove: () =>
        setSlice({
          inclusionMethod: "ANY_PRESENT",
          inclusionVendorIds: [],
          inclusionDepartmentIds: [],
          inclusionRoleIds: [],
          inclusionInstallAgeMonths: undefined,
          inclusionProductCount: undefined,
          excludedVendorIds: slice.excludedVendorIds,
        }),
      tooltipContent,
    });
  }

  // Chip for excluded vendors
  if (slice.excludedVendorIds.length > 0) {
    const excludedNames = lookups?.vendors
      ? mapIdsToNames(
          slice.excludedVendorIds,
          lookups.vendors,
          (id) => `Vendor ${id}`
        )
      : [];

    const label =
      slice.excludedVendorIds.length === 1 && excludedNames[0]
        ? excludedNames[0]
        : `${slice.excludedVendorIds.length} selected`;

    const tooltipContent = lookups?.vendors
      ? renderFilterDetailList("Vendors", excludedNames)
      : undefined;

    const allNames = lookups?.vendors?.length
      ? listNames(excludedNames)
      : undefined;

    chips.push({
      id: "excludedVendors",
      label: `Excluded Vendors: ${label}`,
      summaryLabel: allNames && `Excluded Vendors: ${allNames}`,
      filterKey: "vendors" as const,
      onRemove: () =>
        setSlice({
          ...slice,
          excludedVendorIds: [],
        }),
      tooltipContent,
    });
  }

  return chips;
}

export function getCount(slice: VendorsSlice): number {
  return (
    slice.inclusionVendorIds.length +
    slice.excludedVendorIds.length +
    slice.inclusionDepartmentIds.length +
    slice.inclusionRoleIds.length +
    (slice.inclusionInstallAgeMonths ? 1 : 0) +
    (slice.inclusionProductCount ? 1 : 0)
  );
}
